import { ZodObject, ZodRawShape, ZodReadonly } from "zod";
import { z } from "@/zod";
import { unwrapZodType } from "@/lib/utils/zod";
import { FilterType, mapFilterTypeToFilterFn } from "@/lib/filters";
import { isNotId } from "@/lib/utils/isNotId";
import { ColumnDef } from "@tanstack/react-table";
import { Row } from "@tanstack/react-table";
import i18next from "i18next";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { DateFilter } from "@/components/data-table/components/DateFilter";
import { NumberFilter } from "@/components/data-table/components/NumberFilter";
import { BOOLEAN_VALUES } from "@/constants";
import { NUMBER_SCHEMA } from "@/schemas/commonFields/numberSchema";
import {
  formatBoolean,
  formatDate,
  formatDateTime,
  formatNumber,
} from "@/lib/utils/formatters";

export const getInitialColumnVisibility = <TData>(
  columns: ColumnDef<TData>[],
  initialColumnVisibility: Record<string, boolean> = {},
) => {
  const visibility = columns.reduce(
    (acc, column) => {
      if (column?.id !== undefined) {
        acc[column.id] = isNotId(column.id);
      }
      return acc;
    },
    {} as Record<string, boolean>,
  );
  return {
    ...visibility,
    ...initialColumnVisibility,
  };
};

export const getInitialColumnOrder = <TData>(columns: ColumnDef<TData>[]) => {
  return columns.reduce((acc, column) => {
    if (column.id !== undefined) {
      acc.push(column.id);
    }
    return acc;
  }, [] as string[]);
};

export const mergeColumnState = <T>(
  initialState: Record<string, T>,
  state: Record<string, T>,
): Record<string, T> => {
  return {
    ...initialState,
    ...state,
  };
};

/**
 * Merges column order from localStorage with default order.
 * Preserves user's order for existing columns, inserts new columns in correct positions.
 * Ensures select column is always first.
 */
export const createLatestColumnOrder = (
  defaultOrder: string[],
  order: string[],
): string[] => {
  const storedOrder = order.filter((id) => id !== "select");
  const nonSelectColumns = defaultOrder.filter((id) => id !== "select");

  const result = [...nonSelectColumns].sort((a, b) => {
    const aInStorage = storedOrder.indexOf(a);
    const bInStorage = storedOrder.indexOf(b);

    // Both in localStorage: use their order
    if (aInStorage !== -1 && bInStorage !== -1) {
      return aInStorage - bInStorage;
    }

    // At least one is new: use defaultOrder position
    return defaultOrder.indexOf(a) - defaultOrder.indexOf(b);
  });

  return defaultOrder.includes("select") ? ["select", ...result] : result;
};

const getCellFn = (schema: ZodObject<ZodRawShape>, prop: string) => {
  const zodProp = schema.shape[prop];
  const zodType = unwrapZodType(zodProp);

  if (zodType instanceof z.ZodBoolean) {
    return ({ row }: { row: Row<Record<string, boolean>> }) => {
      const value = row.original[prop];
      if (typeof value !== "boolean") {
        return;
      }
      return formatBoolean(value, i18next.t);
    };
  }

  if (zodType instanceof z.ZodString) {
    const checks = zodType._def.checks;
    const dateCheck = checks?.find((check) => check.kind === "date");

    if (dateCheck) {
      return ({
        row,
      }: {
        row: Row<Record<string, string | null | undefined>>;
      }) => {
        const value = row.original[prop];
        return formatDate(value);
      };
    }

    const datetimeCheck = checks?.find((check) => check.kind === "datetime");
    if (datetimeCheck) {
      return ({
        row,
      }: {
        row: Row<Record<string, string | null | undefined>>;
      }) => {
        const value = row.original[prop];
        return formatDateTime(value);
      };
    }
  }

  if (zodProp?._def?.description === NUMBER_SCHEMA) {
    return ({ row }: { row: Row<Record<string, string | number>> }) => {
      const value = row.original[prop];
      // Only format if value is string or number
      if (typeof value !== "string" && typeof value !== "number") {
        return;
      }
      // Convert dot-based decimal to comma-based (26.66 -> 26,66)
      return formatNumber(value);
    };
  }

  return undefined;
};

const getMetaFn = (schema: ZodObject<ZodRawShape>, prop: string) => {
  const zodProp = schema.shape[prop];
  const zodType = unwrapZodType(zodProp);

  if (zodType instanceof z.ZodBoolean) {
    return {
      filter: {
        component: EnumFilter,
        props: {
          options: BOOLEAN_VALUES,
          translationKey: "booleanValues",
        },
      },
    };
  }

  if (zodType instanceof z.ZodDate) {
    return {
      filter: {
        component: DateFilter,
      },
    };
  }

  if (zodType instanceof z.ZodString) {
    const checks = zodType._def.checks;
    const hasDateCheck = checks?.some(
      (check) => check.kind === "date" || check.kind === "datetime",
    );

    if (hasDateCheck) {
      return {
        filter: {
          component: DateFilter,
        },
      };
    }
  }

  if (zodProp?._def?.description === NUMBER_SCHEMA) {
    return {
      filter: {
        component: NumberFilter,
      },
    };
  }

  return undefined;
};

export const buildColumnsFromSchema = <TData extends { id: string }>(
  schema: ZodReadonly<ZodObject<ZodRawShape>> | ZodObject<ZodRawShape>,
  translationKey: string,
  overrides?: Record<string, ColumnDef<TData, unknown>>,
): ColumnDef<TData>[] => {
  // TODO: after all Read* entities are marked as readonly remove this
  const unwrappedSchema = "unwrap" in schema ? schema.unwrap() : schema;
  const keys = Object.keys(unwrappedSchema.shape);

  const columns = keys.map((prop) => {
    const column: ColumnDef<TData> = {
      id: prop,
      accessorKey: prop,
      header: `${translationKey}.fields.${prop}`,
      enableHiding: isNotId(prop),
      filterFn: mapFilterTypeToFilterFn(FilterType.IncludesString),
    };

    const cell = getCellFn(unwrappedSchema, prop);
    const meta = getMetaFn(unwrappedSchema, prop);

    if (cell) {
      Object.assign(column, { cell });
    }

    if (meta) {
      Object.assign(column, { meta });
    }

    if (overrides?.[prop]) {
      const { meta: overrideMeta, ...restOverride } = overrides[prop];
      return {
        ...column,
        ...restOverride,
        ...(overrideMeta !== undefined && {
          meta: { ...column.meta, ...overrideMeta },
        }),
      };
    }
    return column;
  });

  // Add new columns from overrides that are not in the schema
  if (overrides) {
    for (const [key, override] of Object.entries(overrides)) {
      if (!keys.includes(key)) {
        columns.push({
          id: key,
          accessorKey: key,
          header: `${translationKey}.${key}`,
          enableHiding: isNotId(key),
          filterFn: mapFilterTypeToFilterFn(FilterType.IncludesString),
          ...override,
        });
      }
    }
  }
  return columns;
};
